import { useState, useEffect } from 'react'
import { Boxes, Loader2, Play } from 'lucide-react'
import api from '../../core/services/api'

interface Module {
  id: string
  code: string
  name: string
  description: string | null
  category: string
  icon: string | null
  menu_parent: string | null
  menu_order: number
  is_available: boolean
  is_core: boolean
  is_beta: boolean
}

const categoryLabels: Record<string, string> = {
  core: 'Core',
  sales: 'Vendite',
  purchase: 'Acquisti',
  inventory: 'Magazzino',
  production: 'Produzione',
  accounting: 'Contabilità',
  crm: 'CRM',
  project: 'Progetti',
  hr: 'HR',
  tools: 'Strumenti',
  settings: 'Impostazioni',
  integration: 'Integrazioni',
}

const categoryColors: Record<string, string> = {
  core: 'bg-gray-100 text-gray-700',
  sales: 'bg-green-100 text-green-700',
  purchase: 'bg-orange-100 text-orange-700',
  inventory: 'bg-blue-100 text-blue-700',
  production: 'bg-purple-100 text-purple-700',
  accounting: 'bg-yellow-100 text-yellow-700',
  crm: 'bg-pink-100 text-pink-700',
  project: 'bg-indigo-100 text-indigo-700',
  hr: 'bg-teal-100 text-teal-700',
  tools: 'bg-cyan-100 text-cyan-700',
  settings: 'bg-slate-100 text-slate-700',
  integration: 'bg-red-100 text-red-700',
}

export default function ModulesPage() {
  const [modules, setModules] = useState<Module[]>([])
  const [loading, setLoading] = useState(true)
  const [seeding, setSeeding] = useState(false)

  useEffect(() => {
    loadModules()
  }, [])

  const loadModules = async () => {
    try {
      const response = await api.get('/v1/superadmin/modules')
      setModules(response.data)
    } catch (err) {
      console.error('Errore caricamento moduli:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSeedModules = async () => {
    setSeeding(true)
    try {
      await api.post('/v1/superadmin/seed-modules')
      await loadModules()
    } catch (err) {
      console.error('Errore seed moduli:', err)
    } finally {
      setSeeding(false)
    }
  }

  // Raggruppa per categoria
  const groupedModules = modules.reduce((acc, module) => {
    const cat = module.category
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(module)
    return acc
  }, {} as Record<string, Module[]>)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Registry Moduli</h1>
          <p className="text-gray-500 mt-1">
            Moduli disponibili nel sistema ({modules.length} totali)
          </p>
        </div>
        <button
          onClick={handleSeedModules}
          disabled={seeding}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          {seeding ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Play className="w-5 h-5" />
          )}
          Inizializza Moduli
        </button>
      </div>

      {/* Empty state */}
      {modules.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Boxes className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">Nessun modulo nel registry</p>
          <button
            onClick={handleSeedModules}
            disabled={seeding}
            className="text-primary-600 hover:underline"
          >
            Clicca qui per inizializzare i moduli base
          </button>
        </div>
      ) : (
        /* Module Grid by Category */
        <div className="space-y-8">
          {Object.entries(groupedModules).map(([category, categoryModules]) => (
            <div key={category}>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    categoryColors[category] || 'bg-gray-100'
                  }`}
                >
                  {categoryLabels[category] || category}
                </span>
                <span className="text-gray-400 text-sm font-normal">
                  {categoryModules.length} moduli
                </span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryModules.map((module) => (
                  <div
                    key={module.id}
                    className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <Boxes className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">{module.name}</h3>
                          <p className="text-sm text-gray-500">{module.code}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {module.is_core && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                            Core
                          </span>
                        )}
                        {module.is_beta && (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                            Beta
                          </span>
                        )}
                      </div>
                    </div>

                    {module.description && (
                      <p className="text-sm text-gray-500 mt-3">{module.description}</p>
                    )}

                    <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-gray-500">
                      <span>Menu: {module.menu_parent || '-'}</span>
                      <span>Ordine: {module.menu_order}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
